import { useSimulationStore } from '@/store/useSimulationStore';
import { bus } from '@/store/eventBus';
import { tickAgent } from './AgentBehavior';
import { evolveMetrics, evolveStoreMetrics } from './MetricsEvolver';
import { generateRandomEvent, generateValidationEvent } from './EventGenerator';

interface EngineConfig {
  /** Intervalle entre chaque tick en ms (défaut: 1500) */
  tickIntervalMs?: number;
  /** Probabilité qu'un event aléatoire soit émis par tick (0-1) */
  eventProbability?: number;
}

class SimulationEngine {
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private config: Required<EngineConfig>;

  constructor(config: EngineConfig = {}) {
    this.config = {
      tickIntervalMs: config.tickIntervalMs ?? 1500,
      eventProbability: config.eventProbability ?? 0.4,
    };
  }

  start() {
    if (this.intervalId !== null) return;
    this.intervalId = setInterval(() => this.tick(), this.config.tickIntervalMs);
    console.log('[SimulationEngine] Démarré — tick toutes les', this.config.tickIntervalMs, 'ms');
  }

  stop() {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  setTickInterval(ms: number) {
    this.config.tickIntervalMs = ms;
    if (this.intervalId !== null) {
      this.stop();
      this.start();
    }
  }

  private tick() {
    const store = useSimulationStore.getState();
    if (store.killSwitchActive) return;

    const { agents, rooms, stores, metrics, tasks } = store;

    // ── 1. Faire évoluer chaque agent ──────────────────────────────────
    const updatedAgents = agents.map(agent => {
      const result = tickAgent(agent, rooms);
      const updated = { ...agent, statusDuration: agent.statusDuration + 1 };

      if (result.newStatus) {
        updated.status = result.newStatus;
        updated.statusDuration = 0;
      }
      if (result.newRoomId) {
        updated.currentRoomId = result.newRoomId;
        updated.targetRoomId = result.targetRoomId ?? null;
      }
      if (result.pixelPath) {
        updated.pixelPath = result.pixelPath;
      }
      if (result.needsValidation) {
        // Trouver une tâche en cours pour cet agent
        const task = tasks.find(t => t.agentId === agent.id && t.status === 'in_progress');
        if (task) {
          store.updateTask(task.id, { status: 'waiting_validation', progress: 100 });
          const ev = generateValidationEvent(agent, task.title);
          store.addEvent(ev);
          bus.emit('agent:needs_action', { agentId: agent.id, taskId: task.id });
        } else {
          updated.status = 'working'; // pas de tâche → revenir au travail
          updated.statusDuration = 0;
        }
      }
      return updated;
    });

    store.setAgents(updatedAgents);

    // ── 2. Mettre à jour les salles (présence des agents) ───────────────
    const updatedRooms = rooms.map(room => ({
      ...room,
      agentsPresent: updatedAgents.filter(a => a.currentRoomId === room.id).map(a => a.id),
    }));
    store.setRooms(updatedRooms);

    // ── 3. Faire évoluer les métriques globales ─────────────────────────
    const onlineCount = updatedAgents.filter(a => a.online).length;
    const newMetrics = evolveMetrics(metrics, stores, onlineCount);
    store.setMetrics({
      ...newMetrics,
      activeTasks: tasks.filter(t => t.status === 'in_progress').length,
      pendingValidations: tasks.filter(t => t.status === 'waiting_validation').length,
    });

    // ── 4. Faire évoluer les métriques boutiques (légèrement) ───────────
    if (newMetrics.tick % 3 === 0) {
      const updatedStores = stores.map(evolveStoreMetrics);
      useSimulationStore.setState({ stores: updatedStores });
    }

    // ── 5. Émettre un event aléatoire ───────────────────────────────────
    if (Math.random() < this.config.eventProbability) {
      const event = generateRandomEvent(updatedAgents, stores);
      if (event) store.addEvent(event);
    }

    // ── 6. Signaler le tick au bus ──────────────────────────────────────
    bus.emit('sim:tick', { tick: newMetrics.tick });
  }
}

// Singleton exporté — démarré dans main.tsx
export const simulationEngine = new SimulationEngine();
